'use client'

import * as React from 'react'
import { useFormContext } from 'react-hook-form'
import { FormFieldContext } from './FormFieldContext'
import { FormItemContext } from './FormItemContext'

/**
 * @group Hooks
 * 
 * @summary
 * <blockquote>Hook to retrieve form field related props and state.</blockquote>
 * 
 * @returns Form field props and state, or null if used outside of FormField context
 * 
 * @example
 * ```typescript
 * import { useFormField } from '@shared/ui'
 * 
 * const { error, formItemId } = useFormField()
 * ```
 */
export const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)
  const { getFieldState, formState } = useFormContext()

  if (!fieldContext) {
    return null
  }

  const fieldState = getFieldState(fieldContext.name, formState)
  const { id } = itemContext

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  }
}
